import React, { VFC, FC, useCallback, ReactNode } from 'react';
import { Box, Button, Divider, useTheme } from '@dxb-web-ui-framework/design-system';
import { useAppAuthenticationState } from '@dxb-web-ui-framework/iam/contexts/AppAuthentication/hooks';
import { ArrowBack as ArrowBackIcon } from '@dxb-web-ui-framework/design-system/dist/icons';
import { useNavigate } from 'react-router-dom';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { getRoutePath } from '../../Routes/routePaths';
import { useSelectedCustomerId } from '../../../../../userProfile/src/contexts/UserInfo/hooks';
import { TransactionsNotification } from './TransactionsNotification';

export interface MobileHeaderProps {
  actions?: {
    Component: FC<any>;
    props: any;
    children?: ReactNode;
  }[];
  onBackOverride?: () => void;
  backgroundColor?: { type: string; value: string | number } | string;
  removeDivider?: boolean;
}

const MobileHeader: VFC<MobileHeaderProps> = ({ actions, backgroundColor, removeDivider, onBackOverride }) => {
  //TODO: add extra actions as props when designs requests it
  const { requiredApproved } = useAppAuthenticationState();
  const theme = useTheme();
  const customerId = useSelectedCustomerId();
  const { isExtraSmallScreen: isMobile } = useScreenDetector();
  const navigate = useNavigate();

  const onNavigateBack = useCallback(() => {
    if (onBackOverride) {
      onBackOverride();
    } else {
      navigate(getRoutePath({ routeKey: 'dashboard', customerId }));
    }
  }, [onBackOverride, navigate, customerId]);

  const zIndex = isMobile ? 'drawer' : 0;

  const justifyContent = actions ? 'space-between' : 'unset';

  if (!isMobile) {
    return null;
  }

  return (
    <Box
      component={'header'}
      className={`mui-fixed`}
      position='fixed'
      display='flex'
      flexDirection='column'
      height={theme.spacing(11)}
      bgcolor={
        backgroundColor
          ? typeof backgroundColor === 'object'
            ? // @ts-ignore
              theme.palette[backgroundColor.type as any][backgroundColor.value as any]
            : backgroundColor
          : theme.palette.common.white
      }
      width='100%'
      lineHeight={0}
      zIndex={zIndex}>
      <Box
        display='flex'
        flex={1}
        px={2}
        position='relative'
        justifyContent={justifyContent}
        alignItems='center'
        width='100%'>
        <Button
          aria-label='Back'
          color='secondary'
          isIconButton
          size='small'
          title='Back'
          variant='text'
          data-testid='navigate-back-button-mobile'
          onClick={onNavigateBack}>
          <ArrowBackIcon />
        </Button>
        {!!requiredApproved && <TransactionsNotification />}
        {actions && (
          <Box display={'flex'} alignItems={'center'}>
            {actions.map(({ Component, props, children }, index) => (
              <Component key={`action${index}`} {...props}>
                {children}
              </Component>
            ))}
          </Box>
        )}
      </Box>
      {!removeDivider && <Divider />}
    </Box>
  );
};

export { MobileHeader };
